// The following code is pulled from and inspired by the `bonFHIR` implementation
// https://bonfhir.dev/packages/core/fhir-client#mergers
// https://github.com/bonfhir/bonfhir/blob/1e929e2c67734a813ad62ab0e79606cb1f7652a8/packages/core/src/r4b/mergers/element-merger.ts

import type { Merger } from '#src/merge/mergers/merger.ts'

export function elementMerger(
  matchingKey: string | null | undefined,
  matchers: string[]
): Merger {
  return {
    canMerge: ({ current, incoming, key }) =>
      (matchingKey ? key === matchingKey : true) &&
      (Array.isArray(current) || Array.isArray(incoming)),
    merge: ({ current, incoming, key }, merge) => {
      if (incoming === undefined || incoming === null) {
        return current
      }
      if (!Array.isArray(current) || !Array.isArray(incoming)) {
        return incoming
      }

      const merged = []
      const uniqueMerged = new Set()
      const mergedByMatchers = new Map()

      for (const item of current) {
        if (item) {
          const itemString = JSON.stringify(item)
          // Skip if this exact item already exists in current
          if (uniqueMerged.has(itemString)) {
            continue
          }
          const itemKey = matchers
            .flatMap((matcher) => JSON.stringify(item[matcher]) || [])
            .join('')
          if (itemKey) {
            mergedByMatchers.set(itemKey, item)
          }
          merged.push(item)
          uniqueMerged.add(itemString)
        }
      }

      for (let i = 0; i < incoming.length; i++) {
        const item = incoming[i]
        const itemString = JSON.stringify(item)

        // Skip if this exact item already exists
        if (uniqueMerged.has(itemString)) {
          continue
        }

        const itemKey = matchers
          .flatMap((matcher) => JSON.stringify(item[matcher]) || [])
          .join('')
        if (mergedByMatchers.has(itemKey)) {
          const index = merged.findIndex((m) =>
            matchers.every((matcher) => m[matcher] === item[matcher])
          )
          if (index !== -1) {
            const oldItemString = JSON.stringify(merged[index])
            const mergedItem = merge({
              current: merged[index],
              incoming: item,
              key,
            })
            merged[index] = mergedItem
            uniqueMerged.delete(oldItemString)
            uniqueMerged.add(JSON.stringify(mergedItem))
          }
        } else if (matchers.length === 0) {
          // If there are no matchers, we can just merge the items by index
          const oldItemString = JSON.stringify(merged[i])
          const mergedItem = merge({
            current: merged[i],
            incoming: item,
            key,
          })
          merged[i] = mergedItem
          uniqueMerged.delete(oldItemString)
          uniqueMerged.add(JSON.stringify(mergedItem))
        } else {
          merged.push(item)
          uniqueMerged.add(itemString)
        }
      }

      return merged
    },
  }
}
